import { useForm, FormProvider } from "react-hook-form";
import SelectDropdown from "../features/header/SelectDropdowns";
import { dateOptions } from "./Constants";
import { formatDateMMDDYYYY } from "../utils/date-utils";
import { useLocation } from "react-router-dom";
import { useEffect, useMemo } from "react";

interface DateFilterFormProps {
  selectedValue: { label: string; value: string };
  setSelectedValue: (option: { label: string; value: string }) => void;
  customRange: any;
  setCustomRange: any;
  setPageIndex?: any;
}

export default function DateFilterForm({
  selectedValue,
  setSelectedValue,
  customRange,
  setCustomRange,
  setPageIndex,
}: DateFilterFormProps) {
  const methods = useForm();
  const { setValue } = methods;

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const rawNew = params.get("new_user");
  const isNewUsers = rawNew === "new" || rawNew === "true" || rawNew === "1";

  const limitedOptions = useMemo(() => {
    if (!isNewUsers) return dateOptions;

    const allow = new Set([
      "today",
      "yesterday",
      "last_7_days",
      "last_14_days",
      "last_15_days",
      "custom_date_range",
    ]);

    return dateOptions.filter((opt) => {
      if (allow.has(opt.value)) return true;

      const m =
        /last_(\d+)_days/.exec(opt.value) ||
        /(\d+)_days/.exec(opt.value) ||
        /past_(\d+)_days/.exec(opt.value);
      if (m) return Number(m[1]) <= 15;

      return false;
    });
  }, [isNewUsers]);

  useEffect(() => {
    if (!isNewUsers) return;

    const isAllowed = limitedOptions.some(
      (o) => o.value === selectedValue.value
    );
    if (isAllowed) return;

    const preferred =
      limitedOptions.find((o) => o.value === "last_15_days") ||
      limitedOptions.find((o) => o.value === "last_14_days") ||
      limitedOptions.find((o) => o.value === "last_7_days") ||
      limitedOptions[0];

    if (preferred) {
      setSelectedValue(preferred);
      setValue("filterDate", preferred.value);
      setPageIndex?.(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewUsers, limitedOptions, setSelectedValue, setValue, setPageIndex]);

  const handleOptionChange = (value: string) => {
    const selectedOption = limitedOptions?.find(
      (option) => option.value === value
    );
    if (selectedOption) {
      setSelectedValue(selectedOption);
      setValue("filterDate", selectedOption.value);
      setPageIndex?.(0);
    }
  };

  const handleDateRangeChange = (range: any) => {
    const startData = range.start;
    const endData = range.end;

    if (startData) {
      const jsDateStart = new Date(
        startData.year,
        startData.month - 1,
        startData.day
      );

      const jsDateEnd = new Date(endData.year, endData.month - 1, endData.day);

      const formattedStartDate = formatDateMMDDYYYY(jsDateStart);
      const formattedEndDate = formatDateMMDDYYYY(jsDateEnd);

      setSelectedValue({
        label: `${formattedStartDate} - ${formattedEndDate}`,
        value: "custom_date_range",
      });
      setPageIndex?.(0);
    }
  };

  return (
    <FormProvider {...methods}>
      <form className="w-full">
        <SelectDropdown
          label="Filter Date"
          name="filterDate"
          // options={dateOptions}
          options={limitedOptions}
          defaultValue={selectedValue.label}
          onChange={handleOptionChange}
          onChangeCustom={handleDateRangeChange}
          placeHolder="Choose a date..."
          disabled={false}
          isNewUsers={isNewUsers}
        />
      </form>
    </FormProvider>
  );
}
